import type { WorkspaceTab } from "../workspace-router/state/workspaceStore";

/**
 * action_type → workspace tab mapping.
 *
 * When a new `ActionProposalEvent` arrives over WS, the router auto-navigates
 * to the destination tab and surfaces the `ActionDrawer` at the top of the
 * tab content. Per `ando_ui_v2_rewrite_plan.md` §A.7.
 *
 * The mapping covers the canonical 17 action types from v1 + a default
 * fallback (`"work"`) for unknown / future types so the user never lands on
 * a 404-equivalent.
 *
 * Adding a new action type:
 *   1. Add the BE-side `action_type` string here.
 *   2. Optionally ship a vendor preview in `@ando/ui/ando/actions/`.
 *   3. NO route changes needed — `ActionFormSurface` mounts on every tab.
 */

export const ACTION_TYPE_TO_TAB: Readonly<Record<string, WorkspaceTab>> = {
  // Email / chat / calendar — first-class native tabs
  // BE-canonical wire is ``send_email`` (see
  // ``ando-agent/src/models/action_models.py::ActionType.SEND_EMAIL``);
  // ``email_send_draft`` kept as a legacy alias for any in-flight FE
  // caller that still references it (May 25, 2026 string mismatch fix).
  send_email: "conversations",
  email_send_draft: "conversations",
  calendar_create_event: "calendar",
  // Tasks & automations live in Lavoro
  task_create: "work",
  automation_trigger: "work",
  // Fatture in Cloud
  fic_create_invoice: "finance",
  fic_credit_note: "finance",
  fic_payment: "finance",
  fic_schedule_invoice_email: "conversations",
  // SDI / Cassetto / Conservazione / Peppol (Path C — italian baseline)
  submit_invoice_sdi: "finance",
  register_cassetto: "finance",
  archive_conservazione: "finance",
  send_peppol: "finance",
  // Open Banking — SEPA credit transfer (Acube payments).
  initiate_sepa_payment: "finance",
  // PEC (Aruba / Infocert)
  aruba_pec_send: "conversations",
  infocert_pec_send: "conversations",
  // SaaS CRM → Sales (Jun 2026 split). SAP B1 lives in Amministrazione
  // — orders, invoices, purchase orders, business partners are all
  // administrative ERP operations (Jun 12, 2026 user feedback: "SAP
  // non lo voglio in lavoro! Ma sap deve entrare in amministrazione").
  salesforce_create_lead: "sales",
  salesforce_create_opportunity: "sales",
  salesforce_create_contact: "sales",
  salesforce_create_account: "sales",
  hubspot_create_deal: "sales",
  hubspot_create_contact: "sales",
  // Outbound prospecting cold email (Jun 2026) — the chat-driven cold-email
  // HITL card auto-routes into the Sales working-area where the lead lives.
  send_cold_email: "sales",
  native_capability: "sales",
  sap_create_purchase_order: "finance",
  sap_create_invoice: "finance",
  sap_b1_create_order: "finance",
  sap_b1_create_business_partner: "finance",
  // Document mutations (May 20, 2026 — SOTA fork-first pattern).
  //
  // ``document_edit`` is the legacy in-place edit (overwrite source).
  // ``document_fork_edit`` is the new fork-first variant: source stays
  // immutable, a copy is created in the user's editable workspace and
  // ``edits`` are applied on the fork. Both route to Documenti because
  // the source file is already represented there (artifact list) and the
  // ``SourceDrawerSurface`` artifact mode reuses that hub's drawer.
  //
  // The ActionDrawer (mounted above by ActionFormSurface) shows the diff
  // preview + new_title; the Source drawer (below) shows the base file —
  // so the user sees source + proposed mutation simultaneously, then
  // confirms with one click. Pattern §A.7 + §A.11 of v2 (Action sopra,
  // Source sotto).
  // Salesforce specialized (non-create) survivors + FiC quote — explicit
  // entries for discoverability; the prefix fallback below would route them
  // anyway, but naming them keeps the map self-documenting.
  salesforce_add_note_to_record: "sales",
  salesforce_convert_lead: "sales",
  salesforce_attach_file_to_record: "sales",
  fic_create_quote_or_proforma: "finance",
  document_edit: "documents",
  document_fork_edit: "documents",
  // ``document_create`` is the agent-driven materialisation of a NEW file
  // (e.g. "create me an Excel with these rows"). The resulting artefact
  // is registered under ``content_items.source_type='ai_generated'`` and
  // listed in the Documenti hub — same target tab as edit/fork so the
  // user finds it where they expect (May 21, 2026 — added after the
  // Fabio "where did my generated xlsx go?" incident).
  document_create: "documents",
  // Voice-of-X report generation (Workstream B M2.5, June 17, 2026).
  // The HITL card lets the AI propose creating + running a Voice-of-X
  // report from chat. On confirm the BE creates the report definition
  // AND dispatches the first generation run; the resulting report is
  // surfaced inline via the ``voice_report`` chat embed (M2.4). Routes
  // to ``work`` for now — the dedicated reports hub lands in a later
  // milestone (M2.6); until then the working area is the host.
  voice_report_generate: "work",
  // CAD Big Bang (Jun 12, 2026 SOTA) — every CAD HITL proposal lands in
  // the Tecnico hub, where the drawing/model already lives. Pattern §A.7
  // pinning: action_proposal → tab navigation → ActionDrawer overlay
  // above the existing DxfPreviewStage / CadDataPanel surface so the
  // user reviews the diff right next to the file.
  dxf_modify: "technical",
  dxf_create: "technical",
  extrude_dxf_to_step: "technical",
  revolve_dxf_to_step: "technical",
  cad_modify: "technical",
  cad_create: "technical",
  // Draft-quotation-from-CAD (Jul 10, 2026, issue #903) — the proposal
  // is anchored to a CAD drawing (BOM x price list matching) so it
  // lands in Tecnico where the drawing itself lives, next to the DFM
  // verdict card. Same anchoring rationale as the other CAD entries.
  generate_quotation: "technical",
};

/**
 * Vendor / medium icon family for an action category. The icon module
 * ({@link ./utils/actionTypeIcon}) maps each kind to a lucide glyph; keeping
 * it as an abstract token here means the routing taxonomy stays
 * presentation-free while still being the single source of truth that tab
 * and icon both read from.
 */
export type ActionCategoryIcon =
  | "payment"
  | "fiscal_doc"
  | "certified"
  | "message"
  | "crm"
  | "procurement"
  | "document"
  | "calendar";

/**
 * SHARED prefix taxonomy for dynamic cross-vendor CRUD wire strings
 * (``salesforce_update_lead``, ``fatture_in_cloud_create_cliente``,
 * ``hubspot_update_deal``, …) that the static {@link ACTION_TYPE_TO_TAB}
 * table can't enumerate.
 *
 * This ONE ordered list is consumed by both {@link resolveTabForActionType}
 * (for the destination tab) and ``iconForActionType`` (for the badge glyph)
 * so the two can never diverge: a wire string that routes to ``finance``
 * always gets a fiscal/payment glyph, a PEC string always gets the certified
 * channel glyph, etc. Order matters — first match wins (e.g. ``*payment*``
 * before the broad ``fic_`` fiscal-doc rule).
 */
export interface ActionCategoryRule {
  readonly test: RegExp;
  readonly tab: WorkspaceTab;
  readonly icon: ActionCategoryIcon;
}

export const ACTION_CATEGORY_RULES: ReadonlyArray<ActionCategoryRule> = [
  // Money movement first — ``fic_payment`` / ``initiate_sepa_payment`` /
  // ``*incasso*`` read as a payment BEFORE the broad ``fic_`` fiscal rule.
  { test: /payment|incasso|pagamento|^initiate_sepa/, tab: "finance", icon: "payment" },
  // Conservazione / Peppol — fiscal certified family → Amministrazione tab,
  // certified (Shield) glyph. Listed BEFORE the broad ``fic_`` fiscal rule
  // and before PEC so the Shield glyph wins over Receipt.
  {
    test: /conservazione|archive_conservazione|peppol|send_peppol/,
    tab: "finance",
    icon: "certified",
  },
  // PEC — certified messaging → Conversazioni tab, Shield glyph.
  {
    test: /pec_send|^aruba_pec|^infocert_pec/,
    tab: "conversations",
    icon: "certified",
  },
  // Fatture in Cloud / SDI / Cassetto / SAP invoicing — fiscal documents.
  {
    test: /^(fic_|fatture_in_cloud_|submit_invoice_sdi|register_cassetto)|^sap_.*invoice/,
    tab: "finance",
    icon: "fiscal_doc",
  },
  // Conversazioni: email / messaging — generic message glyph (Mail).
  { test: /^(send_email|email_|send_message)/, tab: "conversations", icon: "message" },
  { test: /^calendar_/, tab: "calendar", icon: "calendar" },
  { test: /^(document_|notion_)/, tab: "documents", icon: "document" },
  // CAD edit/create families (DXF + STEP — Jun 12, 2026 SOTA Big Bang).
  // Routes EVERY ``*dxf*`` / ``*step*`` / ``*cad_*`` proposal to Tecnico
  // so the user reviews the diff next to the drawing.
  { test: /^(dxf_|cad_|extrude_dxf|revolve_dxf|propose_dxf|propose_cad)/, tab: "technical", icon: "document" },
  // CRM (Salesforce / HubSpot) → Sales.
  { test: /^(salesforce_|hubspot_)/, tab: "sales", icon: "crm" },
  // SAP (ERP / procurement) → Lavoro.
  { test: /^sap_/, tab: "work", icon: "procurement" },
];

/**
 * Match the SHARED taxonomy against a wire ``action_type``. Returns the
 * first rule whose ``test`` matches, or ``null`` when none does (caller
 * supplies its own default — ``work`` tab / ``Mail`` icon).
 */
export function actionCategory(actionType: string): ActionCategoryRule | null {
  for (const rule of ACTION_CATEGORY_RULES) {
    if (rule.test.test(actionType)) return rule;
  }
  return null;
}

/**
 * Resolve a tab for a BE wire ``action_type``.
 *
 * The static map covers the canonical fixed strings; generic cross-vendor
 * CRUD ships DYNAMIC wire strings that a static table can't enumerate — so we
 * fall back to the shared {@link ACTION_CATEGORY_RULES} prefix heuristics
 * before the final ``work`` default. Keeps routing correct as new
 * sobjects/verbs land BE-side without a FE map edit each time.
 */
/**
 * Provider → tab fallback (Jun 12, 2026).
 *
 * Used when ``actionType`` is one of the GENERIC enum values (bare
 * ``create_record`` / ``create_event``) so we can still route to the
 * right hub based on the connector that owns the row. Without this,
 * "SAP — Nuovo ordine" (action_type ``create_record``, provider
 * ``sap_b1``) fell through to the ``"work"`` default and the user
 * landed in Lavoro instead of Amministrazione.
 */
const PROVIDER_TO_TAB: Readonly<Record<string, WorkspaceTab>> = {
  sap_b1: "finance",
  salesforce: "sales",
  hubspot: "sales",
  // Native Ando CRM spine (Jul 2026) — crm_* tables, no external vendor.
  ando_crm: "sales",
  fatture_in_cloud: "finance",
  aruba_pec: "conversations",
  infocert_pec: "conversations",
  google_calendar: "calendar",
  outlook_calendar: "calendar",
  outlook: "calendar",
};

export function resolveTabForActionType(
  actionType: string,
  provider?: string | null,
): WorkspaceTab {
  const exact = ACTION_TYPE_TO_TAB[actionType];
  if (exact) return exact;
  if (provider) {
    const byProvider = PROVIDER_TO_TAB[provider];
    if (byProvider) return byProvider;
  }
  return actionCategory(actionType)?.tab ?? "work";
}
